import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageHero from '../components/common/PageHero';
import PrimaryButton from '../components/common/PrimaryButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, FileText, ArrowLeft } from 'lucide-react';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchNewsDetail();
  }, [id]);

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/news/${id}`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHero title="News" />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">News Article Not Found</h1>
          <Link to="/news">
            <Button className="bg-blue-600 hover:bg-blue-700">Back to News</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen">
      <PageHero title="News Article" />
      
      <div className="container mx-auto px-4 py-12">
            {/* Back Button */}
            <button
              className="mb-6 text-[#1e3a8a] hover:text-[#1e40af] font-medium flex items-center"
              onClick={() => navigate('/news')}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to News
            </button>

            <Card>
              <CardContent className="p-8">
                {/* Article Header */}
                <div className="border-b pb-6 mb-6">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {news.title}
                  </h1>
                  <div className="flex items-center text-gray-600 space-x-4">
                    <div className="flex items-center space-x-2">
                      <Calendar size={18} />
                      <span>{formatDate(news.published_at || news.created_at)}</span>
                    </div>
                    {news.category && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {news.category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Featured Image */}
                {news.image_url && (
                  <div className="my-6">
                    <img 
                      src={`${backendUrl}${news.image_url}`}
                      alt={news.title}
                      className="w-full rounded-lg object-cover max-h-96"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}

                {/* Article Content */}
                <div className="prose max-w-none">
                  <div 
                    className="text-gray-700 leading-relaxed space-y-4 whitespace-pre-line"
                    dangerouslySetInnerHTML={{
                      __html: news.content?.replace(
                        /(https?:\/\/[^\s]+)/g,
                        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
                      ) || ''
                    }}
                  />
                </div>

                {/* Attachments */}
                {news.attachments && news.attachments.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Available Files</h3>
                    <div className="space-y-3">
                      {news.attachments.map((attachment, index) => (
                        <a
                          key={index}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <FileText size={24} className="text-blue-600" />
                          <span className="text-gray-900 font-medium">{attachment.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back Link at Bottom */}
                <div className="mt-8 pt-6 border-t">
                  <PrimaryButton
                    variant="outline"
                    onClick={() => navigate('/news')}
                  >
                    <ArrowLeft size={18} className="mr-2" />
                    Back to News
                  </PrimaryButton>
                </div>
              </CardContent>
            </Card>
      </div>
    </div>
  );
};

export default NewsDetail;